/** Past title companies + dispo contacts (Tom 2026-08-31: "an option in lead
 * and transaction that i can choose dispo and title from contacts from past
 * title and contacts i used"). Harvested from every lead's fields and every
 * deal's fields, deduped, most-complete record wins. Pure + testable. */
import type { EditsMap } from "../edits/api";
import type { Deal } from "../deals/api";

export interface PastContact {
  company: string;
  name: string;
  phone: string;
  email: string;
}

export interface PastContacts {
  title: PastContact[];
  dispo: PastContact[];
}

const s = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const digits = (v: string): string => v.replace(/\D/g, "").slice(-10);

/** dedupe key: phone wins (same number = same person), else company+name. */
function keyOf(c: PastContact): string {
  const p = digits(c.phone);
  if (p.length >= 7) return "p:" + p;
  return "n:" + (c.company + "|" + c.name).toLowerCase().replace(/\s+/g, " ").trim();
}

function fill(n: number): number { return n; }
const score = (c: PastContact): number =>
  fill((c.company ? 1 : 0) + (c.name ? 1 : 0) + (c.phone ? 1 : 0) + (c.email ? 1 : 0));

function add(bucket: Map<string, PastContact>, c: PastContact): void {
  if (!c.company && !c.name && !c.phone) return;
  const k = keyOf(c);
  const prev = bucket.get(k);
  if (!prev || score(c) > score(prev)) bucket.set(k, c);
}

const label = (c: PastContact): string =>
  [c.company, c.name].filter(Boolean).join(" · ") || c.phone || c.email;

/** Display label for a picker option. */
export const pastContactLabel = (c: PastContact): string => {
  const head = label(c);
  const tail = c.phone ? " — " + c.phone : c.email ? " — " + c.email : "";
  return head + tail;
};

export function collectPastContacts(edits: EditsMap, deals: Deal[]): PastContacts {
  const title = new Map<string, PastContact>();
  const dispo = new Map<string, PastContact>();
  for (const f of edits.values()) {
    const r = f as Record<string, unknown>;
    add(title, { company: s(r["titleCo"]), name: "", phone: s(r["titlePhone"]), email: s(r["titleEmail"]) });
    add(dispo, { company: s(r["dispCompany"]), name: s(r["dispName"]), phone: s(r["dispPhone"]), email: s(r["dispEmail"]) });
  }
  for (const d of deals) {
    const r = (d.fields ?? {}) as Record<string, unknown>;
    add(title, { company: s(r["t_titleco"]), name: "", phone: s(r["t_titlephone"]), email: s(r["t_titleemail"]) });
    add(dispo, { company: s(r["t_dispco"]), name: s(r["t_dispname"]), phone: s(r["t_dispphone"]), email: s(r["t_dispemail"]) });
  }
  const sort = (m: Map<string, PastContact>): PastContact[] =>
    [...m.values()].sort((a, b) => label(a).localeCompare(label(b))).slice(0, 80);
  return { title: sort(title), dispo: sort(dispo) };
}

/** The field keys each picker fills, per surface. */
export const PAST_KEYS = {
  lead: {
    title: { company: "titleCo", phone: "titlePhone", email: "titleEmail" },
    dispo: { company: "dispCompany", name: "dispName", phone: "dispPhone", email: "dispEmail" },
  },
  deal: {
    title: { company: "t_titleco", phone: "t_titlephone", email: "t_titleemail" },
    dispo: { company: "t_dispco", name: "t_dispname", phone: "t_dispphone", email: "t_dispemail" },
  },
} as const;
